<template>
  <div class="entity-list">
    <div
      class="mt-4 block border-2 rounded border-gray-100"
    >
      <h2
        class="p-2 pl-4 border-b-2 border-gray-100 w-full bg-gray-50 block uppercase tracking-wide text-gray-700 text-xs font-semibold"
      >
        Entities
        <el-popover placement="top-start" trigger="hover">
          <i
            slot="reference"
            class="fas fa-info-circle text-blue ml-1"
          ></i>
          <div class="p-1 leading-normal">
            The entities may not represent the exact
            structure of the generated database.
            <br />New tables/collections and fields might be
            created to satisfy the relationships, field
            types, etc.
          </div>
        </el-popover>

        <el-button
          class="ml-3"
          size="mini"
          type="primary"
          @click="newEntity()"
          ><i class="fas fa-plus mr-1"></i> New
          Entity</el-button
        >

        <el-button
          v-if="showPreDefined && showPreDefinedButton"
          size="mini"
          type="info"
          class="ml-3"
          @click="toggleShowPreDefined()"
          ><i class="fas fa-eye-slash mr-1"></i> Hide
          Pre-Defined</el-button
        >

        <el-button
          v-if="!showPreDefined && showPreDefinedButton"
          size="mini"
          type="info"
          plain
          class="ml-3"
          @click="toggleShowPreDefined()"
          ><i class="fas fa-eye mr-1"></i>Show
          Pre-Defined</el-button
        >
      </h2>

      <div
        v-if="!showPreDefined && !sortedEntities.length"
        class="el-table__empty-block"
        style="height: 100%;"
      >
        <span class="el-table__empty-text">No Data</span>
      </div>

      <el-row
        v-if="showPreDefined || sortedEntities.length"
        style="margin-right: 10px; margin-left: 10px; margin-top: 22px"
        :gutter="24"
      >
        <el-col v-if="showPreDefined" :md="8" :sm="12">
          <el-card shadow="hover" class="box-card">
            <div
              slot="header"
              class="flex items-center justify-between flex-wrap"
            >
              <div class="font-semibold text-lg m-0 my-1">
                tenant
              </div>
              <div class="m-0 my-1">
                <el-button
                  size="small"
                  type="info"
                  plain
                  disabled
                  >Pre-Defined</el-button
                >
              </div>
            </div>

            <ul class="list">
              <li class="mb-1">id: UUID</li>
              <li class="mb-1">name: String/Text</li>
              <li class="mb-1">updatedAt: Datetime</li>
              <li class="mb-1">createdAt: Datetime</li>
            </ul>
          </el-card>
        </el-col>

        <el-col v-if="showPreDefined" :md="8" :sm="12">
          <el-card shadow="hover" class="box-card">
            <div
              slot="header"
              class="flex items-center justify-between flex-wrap"
            >
              <div class="font-semibold text-lg m-0 my-1">
                membership
              </div>
              <div class="m-0 my-1">
                <el-button
                  size="small"
                  type="info"
                  plain
                  disabled
                  >Pre-Defined</el-button
                >
              </div>
            </div>

            <ul class="list">
              <li class="mb-1">id: UUID</li>
              <li class="mb-1">firstName: String/Text</li>
              <li class="mb-1">lastName: String/Text</li>
              <li class="mb-1">avatars: Images</li>
              <li class="mb-1">
                invitationToken: String/Text
              </li>
              <li class="mb-1">
                roles: Array&lt;Enumerator&gt;
              </li>
              <li class="mb-1">status: Enumerator</li>
              <li class="mb-1">updatedAt: Datetime</li>
              <li class="mb-1">createdAt: Datetime</li>
            </ul>
          </el-card>
        </el-col>

        <el-col v-if="showPreDefined" :md="8" :sm="12">
          <el-card shadow="hover" class="box-card">
            <div
              slot="header"
              class="flex items-center justify-between flex-wrap"
            >
              <div class="font-semibold text-lg m-0 my-1">
                user
              </div>
              <div class="m-0 my-1">
                <el-button
                  size="small"
                  type="info"
                  plain
                  disabled
                  >Pre-Defined</el-button
                >
              </div>
            </div>

            <ul class="list">
              <li class="mb-1">id: UUID</li>
              <li class="mb-1">email: String/Text</li>
              <li class="mb-1">password: String/Text</li>
              <li class="mb-1">emailVerified: Boolean</li>
              <li class="mb-1">
                verifyEmailToken: String/Text
              </li>
              <li class="mb-1">
                verifyEmailTokenExpiresAt: Datetime
              </li>
              <li class="mb-1">provider: String/Text</li>
              <li class="mb-1">
                passwordResetToken: String/Text
              </li>
              <li class="mb-1">
                passwordResetTokenExpiresAt: Datetime
              </li>
              <li class="mb-1">
                expireSessionsOlderThan: Datetime
              </li>
              <li class="mb-1">updatedAt: Datetime</li>
              <li class="mb-1">createdAt: Datetime</li>
            </ul>
          </el-card>
        </el-col>

        <el-col v-if="showPreDefined" :md="8" :sm="12">
          <el-card shadow="hover" class="box-card">
            <div
              slot="header"
              class="flex items-center justify-between flex-wrap"
            >
              <div class="font-semibold text-lg m-0 my-1">
                subscription
              </div>
              <div class="m-0 my-1">
                <el-button
                  size="small"
                  type="info"
                  plain
                  disabled
                  >Pre-Defined</el-button
                >
              </div>
            </div>

            <ul class="list">
              <li class="mb-1">id: UUID</li>
              <li class="mb-1">mode: Enumerator</li>
              <li class="mb-1">
                isCancelAtPeriodEnd: Boolean
              </li>
              <li class="mb-1">
                stripeCustomerId: String/Text
              </li>
              <li class="mb-1">
                stripeSubscriptionId: String/Text
              </li>
              <li class="mb-1">
                stripePriceId: String/Text
              </li>
              <li class="mb-1">status: Enumerator</li>
              <li class="mb-1">updatedAt: Datetime</li>
              <li class="mb-1">createdAt: Datetime</li>
            </ul>
          </el-card>
        </el-col>

        <el-col v-if="showPreDefined" :md="8" :sm="12">
          <el-card shadow="hover" class="box-card">
            <div
              slot="header"
              class="flex items-center justify-between flex-wrap"
            >
              <div class="font-semibold text-lg m-0 my-1">
                auditLog
              </div>
              <div class="m-0 my-1">
                <el-button
                  size="small"
                  type="info"
                  plain
                  disabled
                  >Pre-Defined</el-button
                >
              </div>
            </div>

            <ul class="list">
              <li class="mb-1">id: UUID</li>
              <li class="mb-1">timestamp: Datetime</li>
              <li class="mb-1">entityName: String/Text</li>
              <li class="mb-1">entityId: String/Text</li>
              <li class="mb-1">operation: Enumerator</li>
              <li class="mb-1">oldData: JSON</li>
              <li class="mb-1">newData: JSON</li>
              <li class="mb-1">userId: String/Text</li>
              <li class="mb-1">
                membershipId: String/Text
              </li>
              <li class="mb-1">tenantId: String/Text</li>
              <li class="mb-1">apiKeyId: String/Text</li>
              <li class="mb-1">apiEndpoint: String/Text</li>
              <li class="mb-1">
                apiHttpResponseCode: String/Text
              </li>
              <li class="mb-1">
                transactionId: String/Text
              </li>
            </ul>
          </el-card>
        </el-col>

        <el-col v-if="showPreDefined" :md="8" :sm="12">
          <el-card shadow="hover" class="box-card">
            <div
              slot="header"
              class="flex items-center justify-between flex-wrap"
            >
              <div class="font-semibold text-lg m-0 my-1">
                apiKey
              </div>
              <div class="m-0 my-1">
                <el-button
                  size="small"
                  type="info"
                  plain
                  disabled
                  >Pre-Defined</el-button
                >
              </div>
            </div>

            <ul class="list">
              <li class="mb-1">id: UUID</li>
              <li class="mb-1">name: String/Text</li>
              <li class="mb-1">keyPrefix: String/Text</li>
              <li class="mb-1">secret: String/Text</li>
              <li class="mb-1">
                scopes: Enumerator Multiple
              </li>
              <li class="mb-1">expiresAt: Datetime</li>
              <li class="mb-1">disabledAt: Datetime</li>
              <li class="mb-1">updatedAt: Datetime</li>
              <li class="mb-1">createdAt: DateTime</li>
            </ul>
          </el-card>
        </el-col>

        <el-col
          :md="8"
          :sm="12"
          v-for="(entity, index) in sortedEntities"
          :key="entity.id"
        >
          <el-card shadow="hover" class="box-card">
            <div
              slot="header"
              class="flex items-center justify-between flex-wrap"
            >
              <div class="font-semibold text-lg m-0 my-1">
                {{ entity.name.singular }}
              </div>
              <div class="m-0 my-1">
                <el-tooltip
                  placement="top"
                  content="Move Left"
                  effect="light"
                >
                  <el-button
                    :disabled="index === 0"
                    type="text"
                    @click="orderUp(entity)"
                    ><i class="fas fa-chevron-left"></i>
                  </el-button>
                </el-tooltip>
                <el-tooltip
                  placement="top"
                  content="Move Right"
                  effect="light"
                >
                  <el-button
                    :disabled="
                      index === entities.length - 1
                    "
                    type="text"
                    @click="orderDown(entity)"
                    class="mr-1"
                    ><i class="fas fa-chevron-right"></i
                  ></el-button>
                </el-tooltip>
                <el-button
                  size="small"
                  type="primary"
                  plain
                  @click="edit(entity)"
                  >Edit</el-button
                >
                <el-button
                  size="small"
                  type="secondary"
                  plain
                  @click="copy(entity)"
                  >Copy</el-button
                >
                <el-button
                  size="small"
                  type="danger"
                  plain
                  @click="remove(entity)"
                  >Delete</el-button
                >
              </div>
            </div>

            <ul class="list pa0">
              <li v-if="showPreDefined" class="mb-1">
                id: UUID
                <el-tag
                  class="ml1"
                  style="color: #bcbec2"
                  size="mini"
                  type="info"
                  >Pre-Defined</el-tag
                >
              </li>
              <li
                class="mb-1"
                v-for="field in fieldsAndRelationships(
                  entity,
                )"
                :key="field.id"
              >
                {{ fieldLabel(field, entity) }}
              </li>

              <li v-if="showPreDefined" class="mb-1">
                createdAt: Datetime
                <el-tag
                  class="ml1"
                  style="color: #bcbec2"
                  size="mini"
                  type="info"
                  >Pre-Defined</el-tag
                >
              </li>
              <li v-if="showPreDefined" class="mb-1">
                updatedAt: Datetime
                <el-tag
                  class="ml1"
                  style="color: #bcbec2"
                  size="mini"
                  type="info"
                  >Pre-Defined</el-tag
                >
              </li>
            </ul>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters, mapActions } from 'vuex';
import { RelationshipUtils } from '../relationship/relationship-utils';

export default {
  name: 'app-entity-list',

  computed: {
    ...mapGetters({
      data: 'builder/data',
      entities: 'builder/entityCurrent/list',
      showPreDefined: 'builder/showPreDefined',
      scaffold: 'scaffold/current',
      fieldLabel: 'builder/field/label',
      relationships: 'builder/relationshipCurrent/list',
    }),

    fieldsAndRelationships() {
      const relationships = this.relationships;
      return (entity) => {
        const relationshipsFromEntity = RelationshipUtils.selectFromEntity(
          entity,
          relationships,
        );

        const _fieldsAndRelationships = [
          ...(entity.fields || []),
          ...(relationshipsFromEntity || []),
        ];

        _fieldsAndRelationships.sort(
          (a, b) =>
            RelationshipUtils.selectOrder(a, entity) -
            RelationshipUtils.selectOrder(b, entity),
        );

        return _fieldsAndRelationships;
      };
    },

    showPreDefinedButton() {
      return (
        this.scaffold &&
        this.scaffold.version &&
        this.scaffold.version.startsWith('3.')
      );
    },

    sortedEntities() {
      return [...this.entities].sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );
    },
  },

  methods: {
    ...mapActions({
      toggleShowPreDefined: 'builder/toggleShowPreDefined',
    }),

    orderUp(entity) {
      const { dispatch } = this.$store;
      dispatch('builder/entityCurrent/orderUp', entity);
    },

    orderDown(entity) {
      const { dispatch } = this.$store;
      dispatch('builder/entityCurrent/orderDown', entity);
    },

    newEntity() {
      this.$router.push({
        name: 'entity-new',
        params: { scaffoldId: this.scaffold.id },
      });
    },

    copy(entity) {
      const { dispatch } = this.$store;
      dispatch('builder/entityCurrent/copy', entity);
    },

    remove(entity) {
      const { getters, dispatch } = this.$store;

      const matches = getters[
        'builder/entityOrFieldReference/search'
      ](this.data, entity);

      if (matches) {
        const validationErrorMessage = `The entity is in use at: <br/> ${matches.join(
          '<br/>',
        )}`;
        dispatch(
          'core/validationErrorHtml',
          new Error(validationErrorMessage),
        );
        return;
      }

      dispatch(
        'builder/relationshipCurrent/deleteEntity',
        entity,
      );
      dispatch('builder/entityCurrent/delete', entity);
    },

    edit(entity) {
      this.$router.push({
        name: 'entity-edit',
        params: {
          scaffoldId: this.scaffold.id,
          id: entity.id,
          reload: true,
        },
      });
    },
  },
};
</script>

<style></style>
